// ~/apps/themusicchurch-app/frontend/src/pages/Ministries/QuietKindness.jsx

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiHeart,
  FiTruck,
  FiPackage,
  FiUsers,
  FiHome,
  FiShoppingBag,
} from "react-icons/fi";
import Button from "../../components/Common/Button";
import ResponsiveBanner from "../../components/Common/ResponsiveBanner";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding - consistent with other pages
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  background: ${(props) => props.theme.colors.homeNavy};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

const TitleContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.colors.homeText};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const MinistryTitle = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MinistryTagline = styled(motion.p)`
  font-size: ${(props) => props.theme.fontSizes.xl};
  color: ${(props) => props.theme.colors.homeText};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const MainContent = styled(motion.div)``;

const Sidebar = styled(motion.div)``;

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const QuoteBox = styled.blockquote`
  background: ${(props) => props.theme.colors.homeSurface};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  padding: ${(props) => props.theme.spacing.xl};
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  font-style: italic;
  color: ${(props) => props.theme.colors.homeText};
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing["2xl"]};
`;

const ServiceCard = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  padding: ${(props) => props.theme.spacing["2xl"]};
  border-radius: 12px;
  box-shadow: ${(props) => props.theme.shadows.sm};
  text-align: center;
  transition: all ${(props) => props.theme.transitions.fast};
  border: 2px solid transparent;

  &:hover {
    border-color: ${(props) => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${(props) => props.theme.shadows.md};
  }
`;

const ServiceIcon = styled.div`
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  color: ${(props) => props.theme.colors.accent};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ServiceTitle = styled.h4`
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const ServiceDescription = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  font-size: ${(props) => props.theme.fontSizes.sm};
`;

const PrincipleCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  padding: ${(props) => props.theme.spacing.xl};
  border-radius: 12px;
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  text-align: center;
`;

const PrincipleList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${(props) => props.theme.spacing.md};
  margin-top: ${(props) => props.theme.spacing.lg};
`;

const PrincipleItem = styled.div`
  padding: ${(props) => props.theme.spacing.md};
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 8px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.homeGold};
`;

const InfoCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const TruckIcon = styled.div`
  font-size: 100px;
  text-align: center;
  margin: ${(props) => props.theme.spacing.lg} 0;
  opacity: 0.1;
`;

const CTASection = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin-top: ${(props) => props.theme.spacing["4xl"]};
`;

const QuietKindness = () => {
  const services = [
    {
      icon: <FiShoppingBag />,
      title: "Food Distribution",
      description: "Providing meals and groceries to families in need",
    },
    {
      icon: <FiPackage />,
      title: "Essential Resources",
      description: "Distributing clothing, hygiene items, and necessities",
    },
    {
      icon: <FiHome />,
      title: "Housing Support",
      description: "Helping with temporary shelter and housing assistance",
    },
    {
      icon: <FiUsers />,
      title: "Community Aid",
      description: "Supporting local ministries and outreach programs",
    },
    {
      icon: <FiHeart />,
      title: "Encouragement",
      description: "Offering prayer, counseling, and spiritual support",
    },
    {
      icon: <FiTruck />,
      title: "Mobile Outreach",
      description: "Bringing help directly to underserved communities",
    },
  ];

  const principles = [
    "No spotlight",
    "No credit",
    "No fanfare",
    "Just service",
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Quiet Kindness - The Music Church</title>
        <meta
          name="description"
          content="Quiet Kindness - All about quiet acts of service without seeking recognition. Dedicated to meeting needs in practical ways with compassion and humility."
        />
      </Helmet>

      <PageWrapper>
        {/* Title section above the banner */}
        <TitleSection>
          <TitleContent>
            <MinistryTitle
              variants={titleVariants}
              initial="hidden"
              animate="visible"
            >
              Quiet Kindness
            </MinistryTitle>
            <MinistryTagline
              variants={titleVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              Service without spotlight
            </MinistryTagline>
          </TitleContent>
        </TitleSection>

        {/* Banner using ResponsiveBanner */}
        <ResponsiveBanner
          src="/images/home-preview/quiet-kindness-framed-v6.png"
          alt="Quiet Kindness - Service without recognition"
          maxWidth="800px"
          motionProps={{
            initial: { opacity: 0, y: 30 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.8, delay: 0.3 },
          }}
        />

        <ContentSection>
          <ContentGrid>
            <MainContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Section>
                <SectionTitle>About Quiet Kindness</SectionTitle>
                <Text>
                  The Quiet Kindness Ministry is all about quiet acts of service
                  without seeking recognition. This ministry is dedicated to
                  meeting the needs of individuals and communities in practical,
                  impactful ways.
                </Text>
                <Text>
                  From feeding the hungry to providing essential resources,
                  sharing knowledge, and supporting other ministries, we are
                  here to be a helping hand wherever it's needed. Whether it's
                  offering food, assistance, or simply encouragement, we aim to
                  reflect Christ's love by putting others first.
                </Text>
              </Section>

              <PrincipleCard>
                <h3 style={{ marginBottom: "1rem" }}>Our Approach</h3>
                <PrincipleList>
                  {principles.map((principle, index) => (
                    <PrincipleItem key={index}>{principle}</PrincipleItem>
                  ))}
                </PrincipleList>
                <p
                  style={{
                    marginTop: "1.5rem",
                    fontStyle: "italic",
                    color: "#c6d9e2",
                  }}
                >
                  Just service with compassion and humility
                </p>
              </PrincipleCard>

              <Section>
                <SectionTitle>How We Serve</SectionTitle>
                <ServiceGrid>
                  {services.map((service, index) => (
                    <ServiceCard
                      key={index}
                      variants={cardVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <ServiceIcon>{service.icon}</ServiceIcon>
                      <ServiceTitle>{service.title}</ServiceTitle>
                      <ServiceDescription>
                        {service.description}
                      </ServiceDescription>
                    </ServiceCard>
                  ))}
                </ServiceGrid>
              </Section>

              <QuoteBox>
                "But when you give to the needy, do not let your left hand know
                what your right hand is doing, so that your giving may be in
                secret. Then your Father, who sees what is done in secret, will
                reward you."
                <br />- Matthew 6:3-4
              </QuoteBox>

              <Section>
                <SectionTitle>The Heart of Service</SectionTitle>
                <Text>
                  Quiet Kindness operates on the principle that true service
                  doesn't need recognition. We work behind the scenes,
                  partnering with local churches, food banks, shelters, and
                  community organizations to identify and meet real needs.
                </Text>
                <Text>
                  Our volunteers come from all walks of life, united by a common
                  desire to make a difference without making a name for
                  themselves. Every act of service, no matter how small, is an
                  opportunity to show God's love in practical ways.
                </Text>
              </Section>

              <TruckIcon>🚛</TruckIcon>

              <Section>
                <SectionTitle>Partnership Philosophy</SectionTitle>
                <Text>
                  We believe in multiplication through partnership. Rather than
                  duplicating efforts, we come alongside existing ministries and
                  organizations, providing support where it's needed most. This
                  allows us to maximize impact while maintaining our commitment
                  to quiet, humble service.
                </Text>
              </Section>
            </MainContent>

            <Sidebar
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Ways to Help
                </SectionTitle>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  <li
                    style={{
                      padding: "0.5rem 0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FiPackage style={{ color: "#7FCDCD" }} />
                    <span>Donate goods</span>
                  </li>
                  <li
                    style={{
                      padding: "0.5rem 0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FiUsers style={{ color: "#7FCDCD" }} />
                    <span>Volunteer time</span>
                  </li>
                  <li
                    style={{
                      padding: "0.5rem 0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FiTruck style={{ color: "#7FCDCD" }} />
                    <span>Provide transportation</span>
                  </li>
                  <li
                    style={{
                      padding: "0.5rem 0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FiHeart style={{ color: "#7FCDCD" }} />
                    <span>Share resources</span>
                  </li>
                </ul>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Current Needs
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  We're always looking for:
                </Text>
                <ul
                  style={{
                    fontSize: "0.875rem",
                    color: "#c6d9e2",
                    paddingLeft: "1.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <li>Non-perishable food items</li>
                  <li>Hygiene products</li>
                  <li>Children's clothing</li>
                  <li>School supplies</li>
                  <li>Volunteer drivers</li>
                </ul>
              </InfoCard>

              <InfoCard style={{ background: "#0d293b" }}>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Remember
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem", fontStyle: "italic" }}>
                  "The greatest among you will be your servant. For those who
                  exalt themselves will be humbled, and those who humble
                  themselves will be exalted."
                </Text>
                <Text style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                  - Matthew 23:11-12
                </Text>
              </InfoCard>
            </Sidebar>
          </ContentGrid>

          <CTASection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Join Us in Quiet Service
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
              No recognition needed. No credit required. Just a heart willing to
              serve.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="accent"
                size="large"
                icon={<FiHeart />}
                onClick={() => console.log("Volunteer clicked")}
              >
                Volunteer Quietly
              </Button>
              <Button
                variant="outline"
                size="large"
                style={{ borderColor: "white", color: "white" }}
                onClick={() => console.log("Donate clicked")}
              >
                Donate Resources
              </Button>
            </div>
          </CTASection>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default QuietKindness;
